import React, { useEffect, useState, useRef, lazy } from 'react';
import { FadeIn } from './ui/FadeIn';
import { BookOpenIcon, GiftIcon, CheckCircle2Icon, ChevronLeftIcon, ChevronRightIcon, MapPinIcon, BarChart3Icon, SparklesIcon, ShoppingBagIcon, LeafIcon, WrenchIcon } from 'lucide-react';
type CategorySlide = {
  type: 'category';
  name: string;
  tagline: string;
  icon: 'map-pin' | 'bar-chart' | 'sparkles' | 'shopping-bag' | 'leaf' | 'wrench';
  color: string;
  bgColor: string;
  borderColor: string;
};
type BonusSlide = {
  type: 'bonus';
  title: string;
  desc: string;
  image: string;
  price: string;
  categoryColor: string;
};
type Slide = CategorySlide | BonusSlide;
const iconMap = {
  'map-pin': MapPinIcon,
  'bar-chart': BarChart3Icon,
  sparkles: SparklesIcon,
  'shopping-bag': ShoppingBagIcon,
  leaf: LeafIcon,
  wrench: WrenchIcon
};
const carouselSlides: Slide[] = [
// — La Ruta del Insumo —
{
  type: 'category',
  name: 'La Ruta del Insumo',
  tagline: 'El recurso logístico para dejar de buscar y empezar a crear.',
  icon: 'map-pin',
  color: 'text-amber-700',
  bgColor: 'bg-amber-50',
  borderColor: 'border-amber-300'
}, {
  type: 'bonus',
  title: 'Guía de Proveedores Por País',
  desc: 'Lista verificada de proveedores de materia prima y envases confiables.',
  image: 'https://cdn.shopify.com/s/files/1/0981/8108/7598/files/BONUS5.webp?v=1772399926',
  price: '$27',
  categoryColor: 'bg-amber-500'
},
// — Cerebro Financiero —
{
  type: 'category',
  name: 'Cerebro Financiero',
  tagline: 'Herramientas de precisión para que tu pasión sea un negocio rentable.',
  icon: 'bar-chart',
  color: 'text-teal-700',
  bgColor: 'bg-teal-50',
  borderColor: 'border-teal-300'
}, {
  type: 'bonus',
  title: 'Calculadora de Rentabilidad',
  desc: 'Plantilla automática que calcula el costo por gramo y tu ganancia real.',
  image: 'https://cdn.shopify.com/s/files/1/0981/8108/7598/files/BONUS12.webp?v=1772399926',
  price: '$19',
  categoryColor: 'bg-teal-500'
}, {
  type: 'bonus',
  title: 'Cómo Calcular el Precio de tus Blends',
  desc: 'Metodología paso a paso para fijar precios competitivos sin sacrificar margen.',
  image: 'https://cdn.shopify.com/s/files/1/0981/8108/7598/files/BONUS19.webp?v=1772399926',
  price: '$24',
  categoryColor: 'bg-teal-500'
}, {
  type: 'bonus',
  title: '33 Ideas de Negocio con Té',
  desc: '33 estrategias probadas para emprender en diferentes nichos del mercado del té.',
  image: 'https://cdn.shopify.com/s/files/1/0981/8108/7598/files/BONUS18.webp?v=1772399926',
  price: '$22',
  categoryColor: 'bg-teal-500'
},
// — Tés de Experiencia Visual —
{
  type: 'category',
  name: 'Tés de Experiencia Visual',
  tagline: 'Blends que entran por los ojos y se vuelven virales.',
  icon: 'sparkles',
  color: 'text-violet-700',
  bgColor: 'bg-violet-50',
  borderColor: 'border-violet-300'
}, {
  type: 'bonus',
  title: 'Tés de Experiencia Premium',
  desc: 'Colores exóticos y flores que cambian de tono en la taza.',
  image: 'https://cdn.shopify.com/s/files/1/0981/8108/7598/files/BONUS16.webp?v=1772399927',
  price: '$29',
  categoryColor: 'bg-violet-500'
}, {
  type: 'bonus',
  title: '+100 Recetas Extra Premium',
  desc: 'Más de 100 recetas probadas, rentables y de calidad superior.',
  image: 'https://cdn.shopify.com/s/files/1/0981/8108/7598/files/BONUS1.webp?v=1772399926',
  price: '$34',
  categoryColor: 'bg-violet-500'
}, {
  type: 'bonus',
  title: 'Fórmulas Ancestrales',
  desc: 'Mezclas inspiradas en tradiciones milenarias para sanar y agradecer.',
  image: 'https://cdn.shopify.com/s/files/1/0981/8108/7598/files/BONUS6.webp?v=1772399926',
  price: '$18',
  categoryColor: 'bg-violet-500'
},
// — Vitrina Imparable —
{
  type: 'category',
  name: 'Vitrina Imparable',
  tagline: 'Estrategias para que tu marca enamore y venda en cada canal.',
  icon: 'shopping-bag',
  color: 'text-rose-700',
  bgColor: 'bg-rose-50',
  borderColor: 'border-rose-300'
}, {
  type: 'bonus',
  title: 'Guía Visual de Packaging',
  desc: 'Envoltorios artesanales de bajo costo pero alto impacto visual.',
  image: 'https://cdn.shopify.com/s/files/1/0981/8108/7598/files/BONUS2.webp?v=1772399926',
  price: '$22',
  categoryColor: 'bg-rose-500'
}, {
  type: 'bonus',
  title: 'Vender en Instagram y Ferias',
  desc: 'Cómo armar un stand irresistible y cerrar ventas digitales.',
  image: 'https://cdn.shopify.com/s/files/1/0981/8108/7598/files/BONUS4.webp?v=1772399926',
  price: '$29',
  categoryColor: 'bg-rose-500'
},
// — Líneas Especializadas —
{
  type: 'category',
  name: 'Líneas Especializadas',
  tagline: 'Diversifica tu catálogo con productos de alta demanda.',
  icon: 'leaf',
  color: 'text-emerald-700',
  bgColor: 'bg-emerald-50',
  borderColor: 'border-emerald-300'
}, {
  type: 'bonus',
  title: 'Té para el Ciclo Femenino',
  desc: 'Blends específicos para cada etapa del ciclo femenino.',
  image: 'https://cdn.shopify.com/s/files/1/0981/8108/7598/files/BONUS13.webp?v=1772399926',
  price: '$21',
  categoryColor: 'bg-emerald-500'
}, {
  type: 'bonus',
  title: 'Hierbas y Tés Medicinales',
  desc: 'Soluciones naturales para insomnio, ansiedad y digestión.',
  image: 'https://cdn.shopify.com/s/files/1/0981/8108/7598/files/BONUS8.webp?v=1772399926',
  price: '$23',
  categoryColor: 'bg-emerald-500'
}, {
  type: 'bonus',
  title: '+100 Recetas Summer Blends',
  desc: 'Cold Brew y recetas frutales para días de calor.',
  image: 'https://cdn.shopify.com/s/files/1/0981/8108/7598/files/BONUS10.webp?v=1772399926',
  price: '$26',
  categoryColor: 'bg-emerald-500'
}, {
  type: 'bonus',
  title: 'El Negocio de los Souvenirs',
  desc: 'Pedidos grandes para eventos con diseños económicos de lujo.',
  image: 'https://cdn.shopify.com/s/files/1/0981/8108/7598/files/BONUS11.webp?v=1772399926',
  price: '$25',
  categoryColor: 'bg-emerald-500'
}, {
  type: 'bonus',
  title: 'Hierbas para tu Matecito',
  desc: 'Hierbas para un mate más digestivo y energético.',
  image: 'https://cdn.shopify.com/s/files/1/0981/8108/7598/files/BONUS9.webp?v=1772399926',
  price: '$15',
  categoryColor: 'bg-emerald-500'
}, {
  type: 'bonus',
  title: 'Pastelería para el Té',
  desc: 'Recetas fáciles de scones y cookies para el Tea Time completo.',
  image: 'https://cdn.shopify.com/s/files/1/0981/8108/7598/files/BONUS14.webp?v=1772399926',
  price: '$17',
  categoryColor: 'bg-emerald-500'
},
// — Herramientas Pro —
{
  type: 'category',
  name: 'Herramientas Pro',
  tagline: 'El soporte técnico y comunicacional para elevar tu autoridad.',
  icon: 'wrench',
  color: 'text-slate-700',
  bgColor: 'bg-slate-100',
  borderColor: 'border-slate-300'
}, {
  type: 'bonus',
  title: 'Pack Textos para Instagram',
  desc: 'Descripciones listas con storytelling que conectan y venden.',
  image: 'https://cdn.shopify.com/s/files/1/0981/8108/7598/files/BONUS3.webp?v=1772399926',
  price: '$26',
  categoryColor: 'bg-slate-500'
}, {
  type: 'bonus',
  title: 'Propiedades de Hierbas y Frutas',
  desc: 'Diccionario completo de beneficios de cada ingrediente.',
  image: 'https://cdn.shopify.com/s/files/1/0981/8108/7598/files/BONUS7.webp?v=1772399927',
  price: '$21',
  categoryColor: 'bg-slate-500'
}, {
  type: 'bonus',
  title: 'La Despensa de Té Eterna',
  desc: 'Guía técnica para conservar hierbas con aroma y propiedades intactas.',
  image: 'https://cdn.shopify.com/s/files/1/0981/8108/7598/files/BONUS15.webp?v=1772399927',
  price: '$19',
  categoryColor: 'bg-slate-500'
}, {
  type: 'bonus',
  title: 'Glosario Internacional del Té',
  desc: 'Términos, orígenes y cultura para ganar autoridad.',
  image: 'https://cdn.shopify.com/s/files/1/0981/8108/7598/files/BONUS17.webp?v=1772399926',
  price: '$16',
  categoryColor: 'bg-slate-500'
}];
export { carouselSlides, iconMap };
export type { Slide, CategorySlide, BonusSlide };
export function BonusSection() {
  const bonuses = [{
    title: 'La Ruta del Insumo',
    desc: 'Lista verificada de proveedores mayoristas por país.'
  }, {
    title: 'Cerebro Financiero',
    desc: 'Calculadora de Rentabilidad para Blends y Souvenirs.'
  }, {
    title: 'Tés de Experiencia Visual',
    desc: 'Recetario de Matcha Azul, Blooming Tea y Magia Química.'
  }, {
    title: 'Vitrina Imparable',
    desc: 'Guía de Packaging económico de lujo y Plan de Venta en Instagram.'
  }, {
    title: 'Líneas Especializadas',
    desc: 'Té para el Ciclo Femenino, Tés Medicinales, Recetas de Verano y Souvenirs Lucrativos.'
  }, {
    title: 'Herramientas Pro',
    desc: 'Pack de Textos para Vender, Guía Visual de Preparación y Guía de Propiedades.'
  }];
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 4);
  };
  useEffect(() => {
    checkScroll();
    const el = scrollRef.current;
    if (el) el.addEventListener('scroll', checkScroll, {
      passive: true
    });
    return () => {
      if (el) el.removeEventListener('scroll', checkScroll);
    };
  }, []);
  const scroll = (direction: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    const cardWidth = el.querySelector('[data-slide]')?.clientWidth ?? 260;
    el.scrollBy({
      left: direction === 'left' ? -(cardWidth + 16) : cardWidth + 16,
      behavior: 'smooth'
    });
  };
  return <section className="py-14 px-6 lg:px-8 bg-tea-green text-tea-cream" data-id="element-40">
      <div className="max-w-6xl mx-auto" data-id="element-41">
        <FadeIn data-id="element-42">
          <div className="text-center mb-16" data-id="element-43">
            <h2 className="text-3xl md:text-5xl font-heading font-bold text-tea-gold mb-6" data-id="element-44">
              El Sistema Completo
            </h2>
            <p className="text-xl text-tea-sage/90 font-light max-w-2xl mx-auto" data-id="element-45">
              Todo lo que necesitas para lanzar, empaquetado en un solo lugar.
            </p>
          </div>
        </FadeIn>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start" data-id="element-46">
          {/* Main Product */}
          <FadeIn delay={0.2} direction="right" className="lg:col-span-5" data-id="element-47">
            <div className="bg-tea-cream/5 border border-tea-gold/30 rounded-2xl p-6 text-center sticky top-8" data-id="element-48">
              <BookOpenIcon className="w-12 h-12 text-tea-gold mx-auto mb-4" data-id="element-49" />
              <h3 className="text-2xl font-heading font-bold text-white mb-2" data-id="element-50">
                Libro Maestro
              </h3>
              <p className="text-tea-gold text-lg mb-5" data-id="element-51">
                El Negocio del Té (Guía de Emprendimiento 21D)
              </p>
              <img src="https://cdn.shopify.com/s/files/1/0981/8108/7598/files/PRODUCTO.png?v=1772406340" alt="Libro Maestro" className="w-full max-w-xs mx-auto drop-shadow-2xl mb-5" data-id="element-52" />
              <p className="text-tea-sage/80 text-sm font-light" data-id="element-53">
                Tu guía principal paso a paso. Incluye +250 Recetas de blends de
                té y tisanas.
              </p>
            </div>
          </FadeIn>

          {/* Bonuses */}
          <FadeIn delay={0.4} direction="left" className="lg:col-span-7" data-id="element-54">
            <div className="bg-white rounded-2xl p-8 md:p-10 text-tea-charcoal" data-id="element-55">
              <div className="flex items-center mb-8" data-id="element-56">
                <GiftIcon className="w-8 h-8 text-tea-gold mr-4" data-id="element-57" />
                <h3 className="text-2xl font-heading font-bold text-tea-green" data-id="element-58">
                  Ecosistema de 19 Bonus de Aceleración:
                </h3>
              </div>
              <div className="space-y-6" data-id="element-59">
                {bonuses.map((bonus, index) => <div key={index} className="flex items-start p-4 rounded-xl bg-tea-cream/50 border border-tea-sage/30 hover:bg-tea-sage/20 transition-colors" data-id="element-60">
                    <CheckCircle2Icon className="w-6 h-6 text-tea-gold mr-4 flex-shrink-0 mt-0.5" data-id="element-61" />
                    <div data-id="element-62">
                      <h4 className="font-bold text-tea-green mb-1" data-id="element-63">
                        {bonus.title}
                      </h4>
                      <p className="text-tea-charcoal/70 text-sm leading-relaxed" data-id="element-64">
                        {bonus.desc}
                      </p>
                    </div>
                  </div>)}
              </div>
            </div>
          </FadeIn>
        </div>

        {/* Category Carousel */}
        <FadeIn delay={0.6} className="mt-16" data-id="element-65">
          <div className="flex items-center justify-between mb-6" data-id="element-66">
            <p className="text-sm font-bold tracking-widest text-tea-gold uppercase" data-id="element-67">
              Mira todo el ecosistema de herramientas que recibes hoy
            </p>
            <div className="hidden md:flex items-center gap-2" data-id="element-68">
              <button onClick={() => scroll('left')} disabled={!canScrollLeft} className="w-10 h-10 rounded-full border border-tea-gold/40 flex items-center justify-center text-tea-gold transition-all duration-200 hover:bg-tea-gold/20 disabled:opacity-30 disabled:cursor-not-allowed" aria-label="Anterior" data-id="element-69">
                <ChevronLeftIcon className="w-5 h-5" data-id="element-70" />
              </button>
              <button onClick={() => scroll('right')} disabled={!canScrollRight} className="w-10 h-10 rounded-full border border-tea-gold/40 flex items-center justify-center text-tea-gold transition-all duration-200 hover:bg-tea-gold/20 disabled:opacity-30 disabled:cursor-not-allowed" aria-label="Siguiente" data-id="element-71">
                <ChevronRightIcon className="w-5 h-5" data-id="element-72" />
              </button>
            </div>
          </div>

          <div className="relative" data-id="element-73">
            <div className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-tea-green to-transparent z-10 pointer-events-none" style={{
            opacity: canScrollLeft ? 1 : 0,
            transition: 'opacity 0.3s'
          }} data-id="element-74" />
            <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-tea-green to-transparent z-10 pointer-events-none" style={{
            opacity: canScrollRight ? 1 : 0,
            transition: 'opacity 0.3s'
          }} data-id="element-75" />

            <div ref={scrollRef} className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-2" style={{
            scrollbarWidth: 'none',
            msOverflowStyle: 'none'
          }} data-id="element-76">
              {carouselSlides.map((slide, idx) => {
              if (slide.type === 'category') {
                const Icon = iconMap[slide.icon];
                return <div key={`cat-${idx}`} data-slide className="relative flex-shrink-0 w-[65vw] sm:w-[45vw] md:w-[220px] snap-start" data-id="element-77">
                      <div className={`rounded-2xl ${slide.bgColor} ${slide.borderColor} border-2 flex flex-col items-center justify-center p-6 text-center shadow-lg`} style={{
                    minHeight: '340px'
                  }} data-id="element-78">
                        <div className="w-14 h-14 rounded-full bg-white/80 flex items-center justify-center mb-4 shadow-sm" data-id="element-79">
                          <Icon className={`w-7 h-7 ${slide.color}`} data-id="element-80" />
                        </div>
                        <h4 className={`font-heading font-bold text-lg mb-3 leading-tight ${slide.color}`} data-id="element-81">
                          {slide.name}
                        </h4>
                        <p className={`text-xs leading-relaxed opacity-70 ${slide.color}`} data-id="element-82">
                          {slide.tagline}
                        </p>
                      </div>
                    </div>;
              } else {
                return <div key={`bonus-${idx}`} data-slide className="relative flex-shrink-0 w-[55vw] sm:w-[40vw] md:w-[220px] snap-start" data-id="element-83">
                      <div className="rounded-2xl bg-white/10 border border-tea-gold/10 overflow-hidden shadow-lg" style={{
                    minHeight: '340px'
                  }} data-id="element-84">
                        <div className="aspect-[4/3] overflow-hidden relative" data-id="element-85">
                          <img src={slide.image} alt={slide.title} className="w-full h-full object-cover" loading="lazy" data-id="element-86" />
                          <div className={`absolute top-2 right-2 ${slide.categoryColor} text-white text-[10px] font-bold px-2 py-0.5 rounded-full`} data-id="element-87">
                            GRATIS
                          </div>
                        </div>
                        <div className="p-4" data-id="element-88">
                          <h5 className="text-tea-cream font-bold text-sm leading-snug mb-1.5" data-id="element-89">
                            {slide.title}
                          </h5>
                          <p className="text-tea-cream/60 text-xs leading-relaxed" data-id="element-90">
                            {slide.desc}
                          </p>
                        </div>
                      </div>
                    </div>;
              }
            })}
            </div>

            <div className="flex justify-center gap-1 mt-4 md:hidden" data-id="element-91">
              <div className="w-8 h-1 rounded-full bg-tea-gold/60" data-id="element-92" />
              <div className="w-1.5 h-1 rounded-full bg-tea-gold/25" data-id="element-93" />
              <div className="w-1.5 h-1 rounded-full bg-tea-gold/25" data-id="element-94" />
            </div>
          </div>
        </FadeIn>
      </div>
    </section>;
}